import React, {useState, useEffect, useMemo} from "react";
import {
    Box, Typography, Button, Paper, TextField, Chip, IconButton, Tooltip,
    CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel,
    Select, MenuItem, LinearProgress, FormControlLabel, Checkbox, Collapse, Pagination,
    Tabs, Tab
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {API_BASE} from "../../apiBase";
import GroundTruthAnnotator from "./GroundTruthAnnotator";
import ViewerContainer from "../PdfViewer/ViewerContainer/ViewerContainer";

function useAuthHeaders() {
    const token = localStorage.getItem("token");
    return useMemo(() => ({
        Authorization: token ? `Bearer ${token}` : "",
    }), [token]);
}

export default function EvaluationTab() {
    const headers = useAuthHeaders();
    const token = localStorage.getItem("token");

    // Suites state
    const [suites, setSuites] = useState([]);
    const [loadingSuites, setLoadingSuites] = useState(false);
    const [selectedSuite, setSelectedSuite] = useState(null);

    // Documents state
    const [documents, setDocuments] = useState([]);
    const [loadingDocuments, setLoadingDocuments] = useState(false);

    // Create suite dialog
    const [createSuiteOpen, setCreateSuiteOpen] = useState(false);
    const [suiteForm, setSuiteForm] = useState({name: "", description: ""});

    // Edit suite dialog
    const [editSuiteOpen, setEditSuiteOpen] = useState(false);
    const [editSuiteForm, setEditSuiteForm] = useState({name: "", description: ""});
    const [editingSuiteId, setEditingSuiteId] = useState(null);

    // Upload document dialog
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadFiles, setUploadFiles] = useState([]);
    const [uploadingCount, setUploadingCount] = useState(0);

    // Edit document name dialog
    const [editDocNameOpen, setEditDocNameOpen] = useState(false);
    const [editingDocId, setEditingDocId] = useState(null);
    const [editDocName, setEditDocName] = useState("");

    // Run evaluation dialog
    const [runDialogOpen, setRunDialogOpen] = useState(false);
    const [models, setModels] = useState([]);
    const [selectedModels, setSelectedModels] = useState([]);
    const [referenceStrategy, setReferenceStrategy] = useState("cross_validation");
    const [referenceDocId, setReferenceDocId] = useState("");
    const [config, setConfig] = useState({
        modes: ['digital', 'scanned'],  // Test both modes by default
        spatial_locality_modes: [true, false],  // Test both spatial locality modes by default
        spatial_closeness: 100
    });

    // Jobs state
    const [jobs, setJobs] = useState([]);
    const [loadingJobs, setLoadingJobs] = useState(false);

    // Results state
    const [viewResultsDialog, setViewResultsDialog] = useState(false);
    const [selectedResults, setSelectedResults] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});
    const [comparisonDialogOpen, setComparisonDialogOpen] = useState(false);
    const [selectedComparison, setSelectedComparison] = useState(null);
    const [targetPdfFile, setTargetPdfFile] = useState(null);
    const [loadingPdf, setLoadingPdf] = useState(false);

    // Ground truth annotation state
    const [selectedDocument, setSelectedDocument] = useState(null);

    // Suite dashboard state
    const [suiteDashboard, setSuiteDashboard] = useState(null);
    const [loadingDashboard, setLoadingDashboard] = useState(false);

    // Jobs pagination
    const [jobsPage, setJobsPage] = useState(1);
    const jobsPerPage = 5;

    // Suite view tabs (documents vs leaderboard)
    const [suiteTab, setSuiteTab] = useState(0); // 0 = documents, 1 = leaderboard

    // Auto-refresh toggle
    const [autoRefresh, setAutoRefresh] = useState(true);

    // Leaderboard sorting
    const [leaderboardSort, setLeaderboardSort] = useState('accuracy'); // 'accuracy' or 'time'

    // Leaderboard expanded rows (for model combinations)
    const [expandedModels, setExpandedModels] = useState({});

    // Fetch suites
    const fetchSuites = async () => {
        setLoadingSuites(true);
        try {
            const res = await fetch(`${API_BASE}/evaluation/suites`, {headers});
            if (res.ok) {
                const data = await res.json();
                setSuites(data.suites || []);
            }
        } catch (e) {
            console.error("Failed to load suites", e);
        } finally {
            setLoadingSuites(false);
        }
    };

    // Fetch documents for selected suite
    const fetchDocuments = async (suiteId) => {
        setLoadingDocuments(true);
        try {
            const res = await fetch(`${API_BASE}/evaluation/suites/${suiteId}/documents`, {headers});
            if (res.ok) {
                const data = await res.json();
                setDocuments(data.documents || []);
            }
        } catch (e) {
            console.error("Failed to load documents", e);
        } finally {
            setLoadingDocuments(false);
        }
    };


    // Fetch models
    const fetchModels = async () => {
        try {
            const res = await fetch(`${API_BASE}/evaluation/models`, {headers});
            if (res.ok) {
                const data = await res.json();
                setModels(data.models || []);
            }
        } catch (e) {
            console.error("Failed to load models", e);
        }
    };

    // Fetch jobs
    const fetchJobs = async (silent = false) => {
        if (!silent) setLoadingJobs(true);
        try {
            const res = await fetch(`${API_BASE}/evaluation/jobs`, {headers});
            if (res.ok) {
                const data = await res.json();
                setJobs(data.jobs || []);
            } else if (res.status === 401 || res.status === 403) {
                // Auth failed - stop auto-refresh
                setAutoRefresh(false);
                console.warn("Authentication failed, auto-refresh disabled");
            }
        } catch (e) {
            console.error("Failed to load jobs", e);
        } finally {
            if (!silent) setLoadingJobs(false);
        }
    };

    useEffect(() => {
        fetchSuites();
        fetchModels();
        fetchJobs();

        // Auto-refresh jobs every 5 seconds if enabled
        if (autoRefresh) {
            const jobsInterval = setInterval(() => {
                fetchJobs(true); // silent refresh
            }, 5000);

            return () => {
                clearInterval(jobsInterval);
            };
        }
    }, [autoRefresh]);

    useEffect(() => {
        if (selectedSuite) {
            fetchDocuments(selectedSuite.id);
            fetchSuiteDashboard(selectedSuite.id);
            setSuiteTab(0); // Reset to documents tab

            // Auto-refresh suite dashboard every 10 seconds when a suite is selected and auto-refresh is enabled
            if (autoRefresh) {
                const dashboardInterval = setInterval(() => {
                    fetchSuiteDashboard(selectedSuite.id, true); // silent refresh
                }, 10000);

                return () => {
                    clearInterval(dashboardInterval);
                };
            }
        }
    }, [selectedSuite, autoRefresh]);

    // Fetch suite dashboard (champion models, stats)
    const fetchSuiteDashboard = async (suiteId, silent = false) => {
        if (!silent) setLoadingDashboard(true);
        try {
            const res = await fetch(`${API_BASE}/evaluation/suites/${suiteId}/dashboard`, {headers});
            if (res.status === 401 || res.status === 403) {
                // Auth failed - stop auto-refresh
                setAutoRefresh(false);
                console.warn("Authentication failed, auto-refresh disabled");
                return;
            }
            if (res.ok) {
                const data = await res.json();
                setSuiteDashboard(data);
            }
        } catch (e) {
            console.error("Failed to load suite dashboard", e);
        } finally {
            if (!silent) setLoadingDashboard(false);
        }
    };

    // Create suite
    const handleCreateSuite = async () => {
        if (!suiteForm.name) {
            alert("Name required");
            return;
        }

        try {
            const res = await fetch(`${API_BASE}/evaluation/suites`, {
                method: "POST",
                headers: {...headers, "Content-Type": "application/json"},
                body: JSON.stringify(suiteForm)
            });

            if (res.ok) {
                setCreateSuiteOpen(false);
                setSuiteForm({name: "", description: ""});
                fetchSuites();
            } else {
                const data = await res.json();
                alert(data.error || "Failed to create suite");
            }
        } catch (e) {
            alert("Server error");
        }
    };

    // Edit suite
    const handleOpenEditSuite = (suite) => {
        setEditingSuiteId(suite.id);
        setEditSuiteForm({name: suite.name, description: suite.description || ""});
        setEditSuiteOpen(true);
    };

    const handleUpdateSuite = async () => {
        if (!editSuiteForm.name.trim()) {
            alert("Name required");
            return;
        }

        try {
            const res = await fetch(`${API_BASE}/evaluation/suites/${editingSuiteId}`, {
                method: "PUT",
                headers: {...headers, "Content-Type": "application/json"},
                body: JSON.stringify(editSuiteForm)
            });

            if (res.ok) {
                setEditSuiteOpen(false);
                fetchSuites();
                if (selectedSuite?.id === editingSuiteId) {
                    const updatedSuite = await res.json();
                    setSelectedSuite(updatedSuite);
                }
            } else {
                const data = await res.json();
                alert(data.error || "Failed to update suite");
            }
        } catch (e) {
            console.error("Failed to update suite", e);
            alert(`Failed to update suite: ${e.message}`);
        }
    };

    // Delete suite
    const handleDeleteSuite = async (suiteId) => {
        if (!window.confirm("Delete this test suite and all its documents?")) return;

        try {
            const res = await fetch(`${API_BASE}/evaluation/suites/${suiteId}`, {
                method: "DELETE",
                headers
            });

            if (res.ok) {
                fetchSuites();
                if (selectedSuite?.id === suiteId) {
                    setSelectedSuite(null);
                    setDocuments([]);
                }
            } else {
                const data = await res.json();
                alert(`Failed to delete suite: ${data.error || 'Unknown error'}`);
            }
        } catch (e) {
            console.error("Failed to delete suite", e);
            alert(`Failed to delete suite: ${e.message}`);
        }
    };

    // Upload documents
    const handleUploadDocuments = async () => {
        if (uploadFiles.length === 0) {
            alert("Please select at least one PDF file");
            return;
        }

        setUploadingCount(uploadFiles.length);
        let successCount = 0;
        let failCount = 0;

        for (const file of uploadFiles) {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("name", file.name.replace('.pdf', ''));

            try {
                const res = await fetch(`${API_BASE}/evaluation/suites/${selectedSuite.id}/documents`, {
                    method: "POST",
                    headers: {Authorization: headers.Authorization},
                    body: formData
                });

                if (res.ok) {
                    successCount++;
                } else {
                    failCount++;
                }
            } catch (e) {
                failCount++;
            }
            setUploadingCount(prev => prev - 1);
        }

        setUploadDialogOpen(false);
        setUploadFiles([]);
        setUploadingCount(0);

        if (successCount > 0) {
            fetchDocuments(selectedSuite.id);
            fetchSuiteDashboard(selectedSuite.id);
        }

        if (failCount > 0) {
            alert(`Uploaded ${successCount} document(s). ${failCount} failed.`);
        }
    };

    // Edit document name
    const handleOpenEditDocName = (doc) => {
        setEditingDocId(doc.id);
        setEditDocName(doc.name);
        setEditDocNameOpen(true);
    };

    const handleUpdateDocName = async () => {
        if (!editDocName.trim()) {
            alert("Name required");
            return;
        }

        try {
            const res = await fetch(`${API_BASE}/evaluation/documents/${editingDocId}`, {
                method: "PUT",
                headers: {...headers, "Content-Type": "application/json"},
                body: JSON.stringify({name: editDocName})
            });

            if (res.ok) {
                setEditDocNameOpen(false);
                fetchDocuments(selectedSuite.id);
            } else {
                const data = await res.json();
                alert(data.error || "Failed to update document name");
            }
        } catch (e) {
            console.error("Failed to update document name", e);
            alert(`Failed to update document name: ${e.message}`);
        }
    };

    // Delete document
    const handleDeleteDocument = async (docId) => {
        if (!window.confirm("Delete this test document?")) return;

        try {
            const res = await fetch(`${API_BASE}/evaluation/documents/${docId}`, {
                method: "DELETE",
                headers
            });

            if (res.ok) {
                fetchDocuments(selectedSuite.id);
                fetchSuiteDashboard(selectedSuite.id); // Update dashboard stats
            }
        } catch (e) {
            console.error("Failed to delete document", e);
        }
    };

    // Handle ground truth save from annotator
    const handleGroundTruthSaved = (groundTruth) => {
        setSelectedDocument(null);
        fetchDocuments(selectedSuite.id);
        fetchSuiteDashboard(selectedSuite.id); // Update dashboard stats
    };

    // Run evaluation
    const handleRunEvaluation = async () => {
        if (selectedModels.length === 0) {
            alert("Select at least one model");
            return;
        }

        if (referenceStrategy === "single" && !referenceDocId) {
            alert("Select a reference document");
            return;
        }

        const payload = {
            suite_id: selectedSuite.id,
            models: selectedModels,
            reference_strategy: referenceStrategy,
            config
        };

        if (referenceStrategy === "single") {
            payload.reference_doc_id = parseInt(referenceDocId);
        }

        try {
            const res = await fetch(`${API_BASE}/evaluation/run`, {
                method: "POST",
                headers: {...headers, "Content-Type": "application/json"},
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                const data = await res.json();
                setRunDialogOpen(false);
                fetchJobs();
            } else {
                const data = await res.json();
                alert(data.error || "Failed to start evaluation");
            }
        } catch (e) {
            alert("Server error");
        }
    };

    // View results
    const handleViewResults = async (jobId) => {
        try {
            const res = await fetch(`${API_BASE}/evaluation/results/${jobId}`, {headers});
            if (res.ok) {
                const data = await res.json();
                setSelectedResults(data);
                setViewResultsDialog(true);
            }
        } catch (e) {
            console.error("Failed to load results", e);
        }
    };

    // Auto-refresh results when dialog is open
    useEffect(() => {
        if (viewResultsDialog && selectedResults && autoRefresh) {
            const resultsInterval = setInterval(async () => {
                try {
                    const res = await fetch(`${API_BASE}/evaluation/results/${selectedResults.job_id}`, {headers});
                    if (res.ok) {
                        const data = await res.json();
                        setSelectedResults(data);
                    }
                } catch (e) {
                    console.error("Failed to refresh results", e);
                }
            }, 5000);

            return () => {
                clearInterval(resultsInterval);
            };
        }
    }, [viewResultsDialog, selectedResults?.job_id, autoRefresh]);

    // Delete job
    const handleDeleteJob = async (jobId) => {
        try {
            const res = await fetch(`${API_BASE}/evaluation/jobs/${jobId}`, {
                method: "DELETE",
                headers
            });
            if (res.ok) {
                fetchJobs();
            } else {
                const data = await res.json();
                alert(data.error || "Failed to delete job");
            }
        } catch (e) {
            console.error("Failed to delete job", e);
            alert("Failed to delete job");
        }
    };

    return (
        <Paper sx={{p: 3}}>
            <Typography variant="h6" mb={2}>Evaluation Testing</Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
                Test tools across multiple documents with different models and configurations
            </Typography>
            <Paper variant="outlined" sx={{p: 2, mb: 3, bgcolor: "info.50"}}>
                <Typography variant="subtitle2" mb={1}>📋 Workflow:</Typography>
                <Typography variant="body2" component="div">
                    1. <strong>Create a Test Suite</strong> - Collection of similar documents (e.g., invoices, receipts)<br/>
                    2. <strong>Upload PDFs</strong> - Add multiple similar test documents to the suite<br/>
                    3. <strong>Annotate Ground Truth</strong> - Define expected field values for each document (coming soon)<br/>
                    4. <strong>Run Evaluation</strong> - One document is used as reference (with spatial annotations), others are tested<br/>
                    5. <strong>View Results</strong> - Compare extraction accuracy across different models
                </Typography>
            </Paper>

            {/* Suites List */}
            <Box mb={4}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Test Suites</Typography>
                    <Box display="flex" gap={1}>
                        <Button
                            variant="contained"
                            onClick={() => setCreateSuiteOpen(true)}
                        >
                            Create Suite
                        </Button>
                        <IconButton onClick={fetchSuites}>
                            <RefreshIcon/>
                        </IconButton>
                    </Box>
                </Box>

                {loadingSuites ? (
                    <CircularProgress/>
                ) : suites.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No test suites. Create one to get started.
                    </Typography>
                ) : (
                    <Box display="flex" gap={2} flexWrap="wrap">
                        {suites.map(suite => (
                            <Paper
                                key={suite.id}
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    minWidth: 250,
                                    cursor: "pointer",
                                    border: selectedSuite?.id === suite.id ? 2 : 1,
                                    borderColor: selectedSuite?.id === suite.id ? "primary.main" : "divider"
                                }}
                                onClick={() => setSelectedSuite(suite)}
                            >
                                <Typography variant="subtitle1">{suite.name}</Typography>
                                {suite.description && (
                                    <Typography variant="caption" color="text.secondary">
                                        {suite.description}
                                    </Typography>
                                )}
                                <Box mt={1} display="flex" gap={1} alignItems="center">
                                    <Chip size="small" label={`${suite.document_count} docs`}/>
                                    <Box ml="auto" display="flex" gap={0.5}>
                                        <IconButton size="small" onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenEditSuite(suite);
                                        }}>
                                            <EditIcon fontSize="small"/>
                                        </IconButton>
                                        <IconButton size="small" onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteSuite(suite.id);
                                        }}>
                                            <DeleteIcon fontSize="small"/>
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                )}
            </Box>

            {/* Suite Dashboard */}
            {selectedSuite && suiteDashboard && (
                <Box mb={4}>
                    <Typography variant="h6" mb={2}>Suite Dashboard: {selectedSuite.name}</Typography>

                    {loadingDashboard ? (
                        <CircularProgress />
                    ) : (
                        <Box display="grid" gap={2}>
                            {/* Champion Models - Three Categories */}
                            <Paper variant="outlined" sx={{p: 2}}>
                                <Typography variant="subtitle1" mb={2}>🏆 Champion Models</Typography>
                                {suiteDashboard.champions && (suiteDashboard.champions.best_performance || suiteDashboard.champions.best_efficiency || suiteDashboard.champions.best_overall) ? (
                                    <Box display="flex" gap={2} flexWrap="wrap">
                                        {/* Best Performance */}
                                        {suiteDashboard.champions.best_performance && (
                                            <Paper sx={{p: 2, bgcolor: 'success.50', minWidth: 250, flex: 1}}>
                                                <Typography variant="overline" color="success.main" fontWeight="bold">
                                                    🎯 Best Performance
                                                </Typography>
                                                <Typography variant="h6" mt={1}>
                                                    {models.find(m => m.name === suiteDashboard.champions.best_performance.model_name)?.display_name || suiteDashboard.champions.best_performance.model_name}
                                                </Typography>
                                                {suiteDashboard.champions.best_performance.mode && (() => {
                                                    const mode = suiteDashboard.champions.best_performance.mode;
                                                    const [ocrMode, , spatialValue] = mode.split('_');
                                                    const spatial = spatialValue === 'true' || spatialValue === 'True';
                                                    return (
                                                        <Box display="flex" gap={0.5} mt={1}>
                                                            <Chip
                                                                size="small"
                                                                label={ocrMode}
                                                                color={ocrMode === 'scanned' ? 'secondary' : 'default'}
                                                            />
                                                            <Chip
                                                                size="small"
                                                                label={spatial ? 'Spatial ON' : 'Spatial OFF'}
                                                                color={spatial ? 'success' : 'default'}
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                    );
                                                })()}
                                                <Typography variant="h4" color="success.main" mt={1}>
                                                    {(suiteDashboard.champions.best_performance.avg_accuracy * 100).toFixed(1)}%
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Accuracy
                                                </Typography>
                                                <Box mt={1}>
                                                    <Typography variant="caption" display="block">
                                                        Precision: {(suiteDashboard.champions.best_performance.avg_precision * 100).toFixed(1)}%
                                                    </Typography>
                                                    <Typography variant="caption" display="block">
                                                        Recall: {(suiteDashboard.champions.best_performance.avg_recall * 100).toFixed(1)}%
                                                    </Typography>
                                                    <Typography variant="caption" display="block">
                                                        Time: {suiteDashboard.champions.best_performance.avg_time_sec > 0 ? `${suiteDashboard.champions.best_performance.avg_time_sec.toFixed(2)}s` : 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        )}

                                        {/* Best Efficiency */}
                                        {suiteDashboard.champions.best_efficiency && (
                                            <Paper sx={{p: 2, bgcolor: 'info.50', minWidth: 250, flex: 1}}>
                                                <Typography variant="overline" color="info.main" fontWeight="bold">
                                                    ⚡ Best Efficiency
                                                </Typography>
                                                <Typography variant="h6" mt={1}>
                                                    {models.find(m => m.name === suiteDashboard.champions.best_efficiency.model_name)?.display_name || suiteDashboard.champions.best_efficiency.model_name}
                                                </Typography>
                                                {suiteDashboard.champions.best_efficiency.mode && (() => {
                                                    const mode = suiteDashboard.champions.best_efficiency.mode;
                                                    const [ocrMode, , spatialValue] = mode.split('_');
                                                    const spatial = spatialValue === 'true' || spatialValue === 'True';
                                                    return (
                                                        <Box display="flex" gap={0.5} mt={1}>
                                                            <Chip
                                                                size="small"
                                                                label={ocrMode}
                                                                color={ocrMode === 'scanned' ? 'secondary' : 'default'}
                                                            />
                                                            <Chip
                                                                size="small"
                                                                label={spatial ? 'Spatial ON' : 'Spatial OFF'}
                                                                color={spatial ? 'success' : 'default'}
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                    );
                                                })()}
                                                <Typography variant="h4" color="info.main" mt={1}>
                                                    {suiteDashboard.champions.best_efficiency.avg_time_sec.toFixed(2)}s
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Time per document
                                                </Typography>
                                                <Box mt={1}>
                                                    <Typography variant="caption" display="block">
                                                        Accuracy: {(suiteDashboard.champions.best_efficiency.avg_accuracy * 100).toFixed(1)}%
                                                    </Typography>
                                                    <Typography variant="caption" display="block">
                                                        Precision: {(suiteDashboard.champions.best_efficiency.avg_precision * 100).toFixed(1)}%
                                                    </Typography>
                                                    <Typography variant="caption" display="block">
                                                        Recall: {(suiteDashboard.champions.best_efficiency.avg_recall * 100).toFixed(1)}%
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        )}

                                        {/* Best Overall */}
                                        {suiteDashboard.champions.best_overall && (
                                            <Paper sx={{p: 2, bgcolor: 'warning.50', minWidth: 250, flex: 1}}>
                                                <Typography variant="overline" color="warning.main" fontWeight="bold">
                                                    ⭐ Best Overall
                                                </Typography>
                                                <Typography variant="h6" mt={1}>
                                                    {models.find(m => m.name === suiteDashboard.champions.best_overall.model_name)?.display_name || suiteDashboard.champions.best_overall.model_name}
                                                </Typography>
                                                {suiteDashboard.champions.best_overall.mode && (() => {
                                                    const mode = suiteDashboard.champions.best_overall.mode;
                                                    const [ocrMode, , spatialValue] = mode.split('_');
                                                    const spatial = spatialValue === 'true' || spatialValue === 'True';
                                                    return (
                                                        <Box display="flex" gap={0.5} mt={1}>
                                                            <Chip
                                                                size="small"
                                                                label={ocrMode}
                                                                color={ocrMode === 'scanned' ? 'secondary' : 'default'}
                                                            />
                                                            <Chip
                                                                size="small"
                                                                label={spatial ? 'Spatial ON' : 'Spatial OFF'}
                                                                color={spatial ? 'success' : 'default'}
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                    );
                                                })()}
                                                <Box display="flex" gap={2} alignItems="center" mt={1}>
                                                    <Box>
                                                        <Typography variant="h5" color="warning.main">
                                                            {(suiteDashboard.champions.best_overall.avg_accuracy * 100).toFixed(1)}%
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Accuracy
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="h6" color="text.secondary">+</Typography>
                                                    <Box>
                                                        <Typography variant="h5" color="warning.main">
                                                            {suiteDashboard.champions.best_overall.avg_time_sec.toFixed(2)}s
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Time
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Typography variant="caption" color="text.secondary" mt={1} display="block">
                                                    Best balance of accuracy and speed
                                                </Typography>
                                            </Paper>
                                        )}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No evaluations run yet. Run an evaluation to see champion models.
                                    </Typography>
                                )}
                            </Paper>

                            {/* Quick Actions */}
                            <Paper variant="outlined" sx={{p: 2}}>
                                <Typography variant="subtitle1" mb={1}>Quick Actions</Typography>
                                <Box display="flex" gap={1}>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={<PlayArrowIcon />}
                                        onClick={() => setRunDialogOpen(true)}
                                        disabled={documents.length === 0}
                                    >
                                        Run New Evaluation
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => fetchSuiteDashboard(selectedSuite.id)}
                                    >
                                        Refresh Stats
                                    </Button>
                                </Box>
                            </Paper>
                        </Box>
                    )}
                </Box>
            )}

            {/* Suite Content - Documents or Leaderboard */}
            {selectedSuite && (
                <Box mb={4}>
                    <Tabs value={suiteTab} onChange={(e, v) => setSuiteTab(v)} sx={{mb: 2}}>
                        <Tab label="Documents" />
                        <Tab label="Leaderboard" />
                    </Tabs>

                    {/* Documents Tab */}
                    {suiteTab === 0 && (
                        <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6">
                                    Documents in "{selectedSuite.name}"
                                </Typography>
                                <Box display="flex" gap={1}>
                                    <Button
                                        variant="contained"
                                        startIcon={<UploadFileIcon/>}
                                        onClick={() => setUploadDialogOpen(true)}
                                    >
                                        Upload PDF
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={<PlayArrowIcon/>}
                                        onClick={() => setRunDialogOpen(true)}
                                        disabled={documents.length === 0}
                                    >
                                        Run Evaluation
                                    </Button>
                                </Box>
                            </Box>

                    {loadingDocuments ? (
                        <CircularProgress/>
                    ) : documents.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No documents. Upload PDFs and annotate ground truth to get started.
                        </Typography>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Ground Truth Fields</TableCell>
                                        <TableCell>Can Be Reference</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {documents.map(doc => (
                                        <TableRow key={doc.id}>
                                            <TableCell>
                                                {doc.name}
                                                <Tooltip title="Rename">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenEditDocName(doc)}
                                                        sx={{ml: 1}}
                                                    >
                                                        <EditIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                {Object.keys(doc.ground_truth || {}).length > 0 ? (
                                                    <Chip
                                                        size="small"
                                                        label={`${Object.keys(doc.ground_truth).length} fields`}
                                                        color="success"
                                                    />
                                                ) : (
                                                    <Chip size="small" label="Not annotated" color="warning"/>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {doc.can_be_reference ? "Yes" : "No"}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title="Edit Ground Truth">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setSelectedDocument(doc)}
                                                    >
                                                        <EditIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteDocument(doc.id)}
                                                    >
                                                        <DeleteIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                        </Box>
                    )}

                    {/* Leaderboard Tab */}
                    {suiteTab === 1 && (
                        <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Typography variant="h6">
                                        Model Leaderboard - "{selectedSuite.name}"
                                    </Typography>
                                    <FormControl size="small" sx={{minWidth: 150}}>
                                        <InputLabel>Sort By</InputLabel>
                                        <Select
                                            value={leaderboardSort}
                                            label="Sort By"
                                            onChange={(e) => setLeaderboardSort(e.target.value)}
                                        >
                                            <MenuItem value="accuracy">Accuracy (Best)</MenuItem>
                                            <MenuItem value="time">Time (Fastest)</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<PlayArrowIcon/>}
                                    onClick={() => setRunDialogOpen(true)}
                                    disabled={documents.length === 0}
                                >
                                    Run on New Models
                                </Button>
                            </Box>

                            {loadingDashboard ? (
                                <CircularProgress />
                            ) : !suiteDashboard || Object.keys(suiteDashboard.model_stats || {}).length === 0 ? (
                                <Paper variant="outlined" sx={{p: 3, textAlign: 'center'}}>
                                    <Typography variant="body1" color="text.secondary" mb={2}>
                                        No evaluation results yet for this suite
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<PlayArrowIcon/>}
                                        onClick={() => setRunDialogOpen(true)}
                                        disabled={documents.length === 0}
                                    >
                                        Run First Evaluation
                                    </Button>
                                </Paper>
                            ) : (
                                <Box display="grid" gap={2}>
                                    {/* Rankings Table - Grouped by Model */}
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell width={50}></TableCell>
                                                    <TableCell>Rank</TableCell>
                                                    <TableCell>Model</TableCell>
                                                    <TableCell>Best Configuration</TableCell>
                                                    <TableCell>Avg Accuracy</TableCell>
                                                    <TableCell>Avg Time/Doc</TableCell>
                                                    <TableCell>Configs Tested</TableCell>
                                                    <TableCell>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(() => {
                                                    // Group stats by model name
                                                    const modelGroups = {};
                                                    Object.entries(suiteDashboard.model_stats).forEach(([statsKey, stats]) => {
                                                        const modelName = stats.model_name;
                                                        if (!modelGroups[modelName]) {
                                                            modelGroups[modelName] = [];
                                                        }
                                                        modelGroups[modelName].push({ statsKey, ...stats });
                                                    });

                                                    // For each model, find best configuration
                                                    const modelSummaries = Object.entries(modelGroups).map(([modelName, configs]) => {
                                                        const bestConfig = configs.reduce((best, current) => {
                                                            if (leaderboardSort === 'time') {
                                                                return (current.avg_time_sec || Infinity) < (best.avg_time_sec || Infinity) ? current : best;
                                                            } else {
                                                                return current.avg_accuracy > best.avg_accuracy ? current : best;
                                                            }
                                                        });

                                                        return {
                                                            modelName,
                                                            bestConfig,
                                                            allConfigs: configs,
                                                            avgAccuracy: bestConfig.avg_accuracy,
                                                            avgTime: bestConfig.avg_time_sec
                                                        };
                                                    });

                                                    // Sort model summaries
                                                    const sortedModels = modelSummaries.sort((a, b) => {
                                                        if (leaderboardSort === 'time') {
                                                            return (a.avgTime || Infinity) - (b.avgTime || Infinity);
                                                        } else {
                                                            return b.avgAccuracy - a.avgAccuracy;
                                                        }
                                                    });

                                                    return sortedModels.map((modelSummary, index) => {
                                                        const modelInfo = models.find(m => m.name === modelSummary.modelName);
                                                        const displayName = modelInfo?.display_name || modelSummary.modelName;
                                                        const isExpanded = expandedModels[modelSummary.modelName];

                                                        return (
                                                            <React.Fragment key={modelSummary.modelName}>
                                                                {/* Main Model Row */}
                                                                <TableRow sx={{
                                                                    bgcolor: index === 0 ? 'success.50' :
                                                                            index === 1 ? 'grey.50' :
                                                                            index === 2 ? 'warning.50' : 'inherit',
                                                                    '&:hover': { bgcolor: index < 3 ? undefined : 'action.hover' }
                                                                }}>
                                                                    <TableCell>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => setExpandedModels(prev => ({
                                                                                ...prev,
                                                                                [modelSummary.modelName]: !prev[modelSummary.modelName]
                                                                            }))}
                                                                        >
                                                                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                                        </IconButton>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Box display="flex" alignItems="center">
                                                                            {index === 0 && <Typography fontSize="1.2rem">🥇</Typography>}
                                                                            {index === 1 && <Typography fontSize="1.2rem">🥈</Typography>}
                                                                            {index === 2 && <Typography fontSize="1.2rem">🥉</Typography>}
                                                                            {index > 2 && <Typography>{index + 1}</Typography>}
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography fontWeight={index < 3 ? 'bold' : 'normal'}>
                                                                            {displayName}
                                                                        </Typography>
                                                                        {modelInfo && (
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                ${modelInfo.cost_per_1k}/1k tokens
                                                                            </Typography>
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {(() => {
                                                                            const mode = modelSummary.bestConfig.mode || 'digital_spatial_false';
                                                                            const [ocrMode, , spatialValue] = mode.split('_');
                                                                            const spatial = spatialValue === 'true' || spatialValue === 'True';
                                                                            return (
                                                                                <Box display="flex" gap={0.5}>
                                                                                    <Chip
                                                                                        size="small"
                                                                                        label={ocrMode}
                                                                                        color={ocrMode === 'scanned' ? 'secondary' : 'default'}
                                                                                    />
                                                                                    <Chip
                                                                                        size="small"
                                                                                        label={spatial ? 'Spatial ON' : 'Spatial OFF'}
                                                                                        color={spatial ? 'success' : 'default'}
                                                                                        variant="outlined"
                                                                                    />
                                                                                </Box>
                                                                            );
                                                                        })()}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography fontWeight={index < 3 ? 'bold' : 'normal'}>
                                                                            {(modelSummary.avgAccuracy * 100).toFixed(1)}%
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {modelSummary.avgTime ? `${modelSummary.avgTime.toFixed(2)}s` : 'N/A'}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {modelSummary.allConfigs.length} config{modelSummary.allConfigs.length !== 1 ? 's' : ''}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Tooltip title="Run another evaluation with this model">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => {
                                                                                    setSelectedModels([modelSummary.modelName]);
                                                                                    setRunDialogOpen(true);
                                                                                }}
                                                                            >
                                                                                <PlayArrowIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </TableCell>
                                                                </TableRow>

                                                                {/* Expanded Configurations */}
                                                                <TableRow>
                                                                    <TableCell colSpan={8} sx={{ p: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                                                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                                            <Box sx={{ margin: 2 }}>
                                                                                <Typography variant="subtitle2" gutterBottom>
                                                                                    All Tested Configurations
                                                                                </Typography>
                                                                                <Table size="small">
                                                                                    <TableHead>
                                                                                        <TableRow>
                                                                                            <TableCell>Configuration</TableCell>
                                                                                            <TableCell>Accuracy</TableCell>
                                                                                            <TableCell>Precision</TableCell>
                                                                                            <TableCell>Recall</TableCell>
                                                                                            <TableCell>F1</TableCell>
                                                                                            <TableCell>Avg Time</TableCell>
                                                                                            <TableCell>Tests</TableCell>
                                                                                        </TableRow>
                                                                                    </TableHead>
                                                                                    <TableBody>
                                                                                        {modelSummary.allConfigs
                                                                                            .sort((a, b) => {
                                                                                                if (leaderboardSort === 'time') {
                                                                                                    return (a.avg_time_sec || Infinity) - (b.avg_time_sec || Infinity);
                                                                                                } else {
                                                                                                    return b.avg_accuracy - a.avg_accuracy;
                                                                                                }
                                                                                            })
                                                                                            .map((config, configIndex) => {
                                                                                                const mode = config.mode || 'digital_spatial_false';
                                                                                                const [ocrMode, , spatialValue] = mode.split('_');
                                                                                                const spatial = spatialValue === 'true' || spatialValue === 'True';
                                                                                                const isBest = config.statsKey === modelSummary.bestConfig.statsKey;

                                                                                                return (
                                                                                                    <TableRow
                                                                                                        key={config.statsKey}
                                                                                                        sx={{ bgcolor: isBest ? 'primary.50' : 'inherit' }}
                                                                                                    >
                                                                                                        <TableCell>
                                                                                                            <Box display="flex" gap={0.5} alignItems="center">
                                                                                                                {isBest && <Typography variant="caption" mr={0.5}>⭐</Typography>}
                                                                                                                <Chip
                                                                                                                    size="small"
                                                                                                                    label={ocrMode}
                                                                                                                    color={ocrMode === 'scanned' ? 'secondary' : 'default'}
                                                                                                                />
                                                                                                                <Chip
                                                                                                                    size="small"
                                                                                                                    label={spatial ? 'Spatial ON' : 'Spatial OFF'}
                                                                                                                    color={spatial ? 'success' : 'default'}
                                                                                                                    variant="outlined"
                                                                                                                />
                                                                                                            </Box>
                                                                                                        </TableCell>
                                                                                                        <TableCell>
                                                                                                            <Typography fontWeight={isBest ? 'bold' : 'normal'}>
                                                                                                                {(config.avg_accuracy * 100).toFixed(1)}%
                                                                                                            </Typography>
                                                                                                        </TableCell>
                                                                                                        <TableCell>{(config.avg_precision * 100).toFixed(1)}%</TableCell>
                                                                                                        <TableCell>{(config.avg_recall * 100).toFixed(1)}%</TableCell>
                                                                                                        <TableCell>{(config.avg_f1 * 100).toFixed(1)}%</TableCell>
                                                                                                        <TableCell>
                                                                                                            {config.avg_time_sec ? `${config.avg_time_sec.toFixed(2)}s` : 'N/A'}
                                                                                                        </TableCell>
                                                                                                        <TableCell>{config.test_count}</TableCell>
                                                                                                    </TableRow>
                                                                                                );
                                                                                            })}
                                                                                    </TableBody>
                                                                                </Table>
                                                                            </Box>
                                                                        </Collapse>
                                                                    </TableCell>
                                                                </TableRow>
                                                            </React.Fragment>
                                                        );
                                                    });
                                                })()}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    {/* Model Selection for Quick Run */}
                                    <Paper variant="outlined" sx={{p: 2}}>
                                        <Typography variant="subtitle1" mb={1}>
                                            Test Additional Models
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" mb={2}>
                                            Select models that haven't been tested yet or run additional tests on existing models.
                                        </Typography>
                                        <Box display="flex" gap={1} flexWrap="wrap">
                                            {models.map(model => {
                                                const alreadyTested = model.name in (suiteDashboard.model_stats || {});
                                                return (
                                                    <Chip
                                                        key={model.name}
                                                        label={`${model.display_name}${alreadyTested ? ' ✓' : ''}`}
                                                        onClick={() => {
                                                            setSelectedModels([model.name]);
                                                            setRunDialogOpen(true);
                                                        }}
                                                        color={alreadyTested ? "default" : "primary"}
                                                        variant={alreadyTested ? "outlined" : "filled"}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    </Paper>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            )}

            {/* Recent Jobs */}
            <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Recent Evaluation Jobs</Typography>
                    <Box display="flex" gap={1} alignItems="center">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={autoRefresh}
                                    onChange={(e) => setAutoRefresh(e.target.checked)}
                                    size="small"
                                />
                            }
                            label={<Typography variant="caption">Auto-refresh</Typography>}
                        />
                        <IconButton onClick={fetchJobs}>
                            <RefreshIcon/>
                        </IconButton>
                    </Box>
                </Box>

                {loadingJobs ? (
                    <CircularProgress/>
                ) : jobs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No evaluation jobs yet.
                    </Typography>
                ) : (
                    <Box>
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Suite</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Strategy</TableCell>
                                        <TableCell>Progress</TableCell>
                                        <TableCell>Created</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {jobs
                                        .slice((jobsPage - 1) * jobsPerPage, jobsPage * jobsPerPage)
                                        .map(job => (
                                    <TableRow key={job.id}>
                                        <TableCell>{job.suite_name}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={job.status}
                                                color={
                                                    job.status === "completed" ? "success" :
                                                        job.status === "failed" ? "error" :
                                                            job.status === "running" ? "primary" : "default"
                                                }
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{job.reference_strategy}</TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={(job.progress / job.total) * 100}
                                                    sx={{width: 100}}
                                                />
                                                <Typography variant="caption">
                                                    {job.progress}/{job.total}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {new Date(job.created_at + 'Z').toLocaleString('de-CH', {timeZone: 'Europe/Zurich'})}
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" gap={1}>
                                                {job.status === "completed" && (
                                                    <Button
                                                        size="small"
                                                        onClick={() => handleViewResults(job.id)}
                                                    >
                                                        View Results
                                                    </Button>
                                                )}
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteJob(job.id)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box display="flex" justifyContent="center" mt={2}>
                        <Pagination
                            count={Math.ceil(jobs.length / jobsPerPage)}
                            page={jobsPage}
                            onChange={(e, page) => setJobsPage(page)}
                            color="primary"
                        />
                    </Box>
                    </Box>
                )}
            </Box>

            {/* Create Suite Dialog */}
            <Dialog open={createSuiteOpen} onClose={() => setCreateSuiteOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create Test Suite</DialogTitle>
                <DialogContent>
                    <Box display="grid" gap={2} pt={1}>
                        <TextField
                            label="Suite Name"
                            value={suiteForm.name}
                            onChange={(e) => setSuiteForm({...suiteForm, name: e.target.value})}
                            fullWidth
                            placeholder="e.g., Invoice Test Suite"
                        />
                        <TextField
                            label="Description"
                            value={suiteForm.description}
                            onChange={(e) => setSuiteForm({...suiteForm, description: e.target.value})}
                            fullWidth
                            multiline
                            rows={2}
                            placeholder="Collection of similar documents for testing extraction"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateSuiteOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateSuite}>Create</Button>
                </DialogActions>
            </Dialog>

            {/* Edit Suite Dialog */}
            <Dialog open={editSuiteOpen} onClose={() => setEditSuiteOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Test Suite</DialogTitle>
                <DialogContent>
                    <Box display="grid" gap={2} pt={1}>
                        <TextField
                            label="Suite Name"
                            value={editSuiteForm.name}
                            onChange={(e) => setEditSuiteForm({...editSuiteForm, name: e.target.value})}
                            fullWidth
                        />
                        <TextField
                            label="Description"
                            value={editSuiteForm.description}
                            onChange={(e) => setEditSuiteForm({...editSuiteForm, description: e.target.value})}
                            fullWidth
                            multiline
                            rows={2}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditSuiteOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleUpdateSuite}>Update</Button>
                </DialogActions>
            </Dialog>

            {/* Upload Document Dialog */}
            <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Upload Test Documents</DialogTitle>
                <DialogContent>
                    <Box display="grid" gap={2} pt={1}>
                        <Button variant="outlined" component="label">
                            Select PDFs (multiple files supported)
                            <input
                                type="file"
                                accept="application/pdf"
                                multiple
                                hidden
                                onChange={(e) => {
                                    const files = Array.from(e.target.files);
                                    setUploadFiles(files);
                                }}
                            />
                        </Button>
                        {uploadFiles.length > 0 && (
                            <Box>
                                <Typography variant="caption" fontWeight="bold">
                                    Selected files ({uploadFiles.length}):
                                </Typography>
                                {uploadFiles.map((file, idx) => (
                                    <Typography key={idx} variant="caption" display="block" ml={1}>
                                        • {file.name}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                        {uploadingCount > 0 && (
                            <Box>
                                <LinearProgress />
                                <Typography variant="caption" color="text.secondary" mt={1}>
                                    Uploading... {uploadingCount} remaining
                                </Typography>
                            </Box>
                        )}
                        <Typography variant="caption" color="text.secondary">
                            Note: After uploading, you'll need to annotate the ground truth for each document
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadDialogOpen(false)} disabled={uploadingCount > 0}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleUploadDocuments}
                        disabled={uploadingCount > 0 || uploadFiles.length === 0}
                    >
                        Upload {uploadFiles.length > 0 ? `(${uploadFiles.length})` : ''}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Document Name Dialog */}
            <Dialog open={editDocNameOpen} onClose={() => setEditDocNameOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Rename Document</DialogTitle>
                <DialogContent>
                    <Box display="grid" gap={2} pt={1}>
                        <TextField
                            label="Document Name"
                            value={editDocName}
                            onChange={(e) => setEditDocName(e.target.value)}
                            fullWidth
                            autoFocus
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDocNameOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleUpdateDocName}>Update</Button>
                </DialogActions>
            </Dialog>

            {/* Run Evaluation Dialog */}
            <Dialog open={runDialogOpen} onClose={() => setRunDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Run Evaluation</DialogTitle>
                <DialogContent>
                    <Box display="grid" gap={3} pt={1}>
                        <FormControl fullWidth>
                            <InputLabel>Reference Strategy</InputLabel>
                            <Select
                                value={referenceStrategy}
                                onChange={(e) => setReferenceStrategy(e.target.value)}
                                label="Reference Strategy"
                            >
                                <MenuItem value="cross_validation">
                                    Cross-Validation (Each doc as reference once)
                                </MenuItem>
                                <MenuItem value="single">
                                    Single Reference Document
                                </MenuItem>
                            </Select>
                        </FormControl>

                        {referenceStrategy === "single" && (
                            <FormControl fullWidth>
                                <InputLabel>Reference Document</InputLabel>
                                <Select
                                    value={referenceDocId}
                                    onChange={(e) => setReferenceDocId(e.target.value)}
                                    label="Reference Document"
                                >
                                    {documents.filter(d => d.can_be_reference).map(doc => (
                                        <MenuItem key={doc.id} value={doc.id}>{doc.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        <Box>
                            <Typography variant="subtitle2" mb={1}>Select Models to Test</Typography>
                            <Box display="flex" gap={1} flexWrap="wrap">
                                {models.map(model => (
                                    <Chip
                                        key={model.name}
                                        label={`${model.display_name} ($${model.cost_per_1k}/1k)`}
                                        onClick={() => {
                                            setSelectedModels(prev =>
                                                prev.includes(model.name)
                                                    ? prev.filter(m => m !== model.name)
                                                    : [...prev, model.name]
                                            );
                                        }}
                                        color={selectedModels.includes(model.name) ? "primary" : "default"}
                                        variant={selectedModels.includes(model.name) ? "filled" : "outlined"}
                                    />
                                ))}
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" mb={1}>OCR Modes to Test</Typography>
                            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                                <FormControlLabel
                                    control={<Checkbox
                                        checked={config.modes.includes('digital')}
                                        onChange={(e) => {
                                            const newModes = e.target.checked
                                                ? [...config.modes, 'digital']
                                                : config.modes.filter(m => m !== 'digital');
                                            setConfig({...config, modes: newModes});
                                        }}
                                    />}
                                    label="Digital (pdfplumber)"
                                />
                                <FormControlLabel
                                    control={<Checkbox
                                        checked={config.modes.includes('scanned')}
                                        onChange={(e) => {
                                            const newModes = e.target.checked
                                                ? [...config.modes, 'scanned']
                                                : config.modes.filter(m => m !== 'scanned');
                                            setConfig({...config, modes: newModes});
                                        }}
                                    />}
                                    label="Scanned (OCR)"
                                />
                            </Box>
                            <Typography variant="subtitle2" mb={1}>Spatial Locality Modes to Test</Typography>
                            <Box display="flex" gap={2} flexWrap="wrap">
                                <FormControlLabel
                                    control={<Checkbox
                                        checked={config.spatial_locality_modes.includes(true)}
                                        onChange={(e) => {
                                            const newModes = e.target.checked
                                                ? [...config.spatial_locality_modes, true]
                                                : config.spatial_locality_modes.filter(m => m !== true);
                                            setConfig({...config, spatial_locality_modes: newModes});
                                        }}
                                    />}
                                    label="Spatial Locality ON"
                                />
                                <FormControlLabel
                                    control={<Checkbox
                                        checked={config.spatial_locality_modes.includes(false)}
                                        onChange={(e) => {
                                            const newModes = e.target.checked
                                                ? [...config.spatial_locality_modes, false]
                                                : config.spatial_locality_modes.filter(m => m !== false);
                                            setConfig({...config, spatial_locality_modes: newModes});
                                        }}
                                    />}
                                    label="Spatial Locality OFF"
                                />
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRunDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleRunEvaluation}>
                        Start Evaluation
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Ground Truth Annotator */}
            <GroundTruthAnnotator
                document={selectedDocument}
                onClose={() => setSelectedDocument(null)}
                onSave={handleGroundTruthSaved}
            />

            {/* Results Dialog */}
            <Dialog open={viewResultsDialog} onClose={() => setViewResultsDialog(false)} maxWidth="lg" fullWidth>
                <DialogTitle>Evaluation Results</DialogTitle>
                <DialogContent>
                    {selectedResults && (
                        <Box display="grid" gap={3}>
                            {/* Summary */}
                            <Box>
                                <Typography variant="h6" mb={2}>Summary</Typography>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Model</TableCell>
                                                <TableCell>Avg Accuracy</TableCell>
                                                <TableCell>Tests Run</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.entries(selectedResults.summary || {}).map(([modelName, summary]) => (
                                                <TableRow key={modelName}>
                                                    <TableCell>{modelName}</TableCell>
                                                    <TableCell>{(summary.avg_accuracy * 100).toFixed(1)}%</TableCell>
                                                    <TableCell>{summary.test_count}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>

                            {/* Detailed Results */}
                            {Object.entries(selectedResults.results_by_model || {}).map(([modelName, results]) => {
                                const toggleRow = (idx) => {
                                    setExpandedRows(prev => ({...prev, [idx]: !prev[idx]}));
                                };

                                return (
                                    <Box key={modelName}>
                                        <Typography variant="h6" mb={2}>{modelName} - Detailed Results</Typography>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell width={50} />
                                                        <TableCell>Reference Doc</TableCell>
                                                        <TableCell>Target Doc</TableCell>
                                                        <TableCell>Mode</TableCell>
                                                        <TableCell>Accuracy</TableCell>
                                                        <TableCell>Precision</TableCell>
                                                        <TableCell>Recall</TableCell>
                                                        <TableCell>F1</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {results.map((result, idx) => (
                                                        <React.Fragment key={idx}>
                                                            <TableRow>
                                                                <TableCell>
                                                                    <IconButton size="small" onClick={() => toggleRow(idx)}>
                                                                        {expandedRows[idx] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                                    </IconButton>
                                                                </TableCell>
                                                                <TableCell>{result.reference_doc_name || "—"}</TableCell>
                                                                <TableCell>{result.target_doc_name}</TableCell>
                                                                <TableCell>
                                                                    {(() => {
                                                                        const mode = result.mode || 'digital_spatial_false';
                                                                        const [ocrMode, , spatialValue] = mode.split('_');
                                                                        const spatial = spatialValue === 'true' || spatialValue === 'True';
                                                                        return (
                                                                            <Box display="flex" gap={0.5}>
                                                                                <Chip
                                                                                    size="small"
                                                                                    label={ocrMode}
                                                                                    color={ocrMode === 'scanned' ? 'secondary' : 'default'}
                                                                                />
                                                                                <Chip
                                                                                    size="small"
                                                                                    label={spatial ? 'Spatial ON' : 'Spatial OFF'}
                                                                                    color={spatial ? 'success' : 'default'}
                                                                                    variant="outlined"
                                                                                />
                                                                            </Box>
                                                                        );
                                                                    })()}
                                                                </TableCell>
                                                                <TableCell>{(result.metrics.accuracy * 100).toFixed(1)}%</TableCell>
                                                                <TableCell>{(result.metrics.precision * 100).toFixed(1)}%</TableCell>
                                                                <TableCell>{(result.metrics.recall * 100).toFixed(1)}%</TableCell>
                                                                <TableCell>{(result.metrics.f1 * 100).toFixed(1)}%</TableCell>
                                                            </TableRow>
                                                            <TableRow>
                                                                <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={7}>
                                                                    <Collapse in={expandedRows[idx]} timeout="auto" unmountOnExit>
                                                                        <Box sx={{margin: 2}}>
                                                                            <Typography variant="subtitle2" gutterBottom>
                                                                                Field-Level Details
                                                                            </Typography>
                                                                            <Table size="small">
                                                                                <TableHead>
                                                                                    <TableRow>
                                                                                        <TableCell>Field</TableCell>
                                                                                        <TableCell>Expected</TableCell>
                                                                                        <TableCell>Extracted</TableCell>
                                                                                        <TableCell>Match</TableCell>
                                                                                        <TableCell>Similarity</TableCell>
                                                                                    </TableRow>
                                                                                </TableHead>
                                                                                <TableBody>
                                                                                    {Object.entries(result.metrics.field_accuracy || {}).map(([fieldName, fieldData]) => (
                                                                                        <TableRow key={fieldName}>
                                                                                            <TableCell><strong>{fieldName}</strong></TableCell>
                                                                                            <TableCell>{fieldData.expected || "—"}</TableCell>
                                                                                            <TableCell>{fieldData.extracted || "—"}</TableCell>
                                                                                            <TableCell>
                                                                                                {fieldData.match ? (
                                                                                                    <CheckCircleIcon fontSize="small" color="success" />
                                                                                                ) : (
                                                                                                    <CancelIcon fontSize="small" color="error" />
                                                                                                )}
                                                                                            </TableCell>
                                                                                            <TableCell>{(fieldData.similarity * 100).toFixed(0)}%</TableCell>
                                                                                        </TableRow>
                                                                                    ))}
                                                                                </TableBody>
                                                                            </Table>
                                                                            <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    TP: {result.metrics.true_positives} |
                                                                                    FP: {result.metrics.false_positives} |
                                                                                    FN: {result.metrics.false_negatives} |
                                                                                    Total Fields: {result.metrics.total_fields}
                                                                                </Typography>
                                                                                <Button
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    onClick={async () => {
                                                                                        setSelectedComparison(result);
                                                                                        setComparisonDialogOpen(true);

                                                                                        // Load target document PDF as File object
                                                                                        setLoadingPdf(true);
                                                                                        try {
                                                                                            const targetUrl = `${API_BASE}/evaluation/test-documents/${result.target_doc_id}/pdf?token=${token}`;
                                                                                            const targetResponse = await fetch(targetUrl);
                                                                                            const targetBlob = await targetResponse.blob();
                                                                                            const targetFile = new File([targetBlob], result.target_doc_name + '.pdf', {type: 'application/pdf'});
                                                                                            setTargetPdfFile(targetFile);
                                                                                        } catch (error) {
                                                                                            console.error('Error loading PDF:', error);
                                                                                        } finally {
                                                                                            setLoadingPdf(false);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    View Documents
                                                                                </Button>
                                                                            </Box>
                                                                        </Box>
                                                                    </Collapse>
                                                                </TableCell>
                                                            </TableRow>
                                                        </React.Fragment>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewResultsDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Document Comparison Dialog */}
            <Dialog
                open={comparisonDialogOpen}
                onClose={() => {
                    setComparisonDialogOpen(false);
                    setTargetPdfFile(null);
                }}
                maxWidth="xl"
                fullWidth
            >
                <DialogTitle>
                    <Box>
                        <Typography variant="h6">
                            Document Comparison: {selectedComparison?.target_doc_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Reference document: {selectedComparison?.reference_doc_name}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {loadingPdf ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
                            <CircularProgress />
                        </Box>
                    ) : selectedComparison && targetPdfFile ? (
                        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} height="80vh">
                            {/* Left: Target Document with its Ground Truth Annotations */}
                            <Box display="flex" flexDirection="column">
                                <Typography variant="subtitle1" gutterBottom>
                                    Ground Truth
                                </Typography>
                                <Box sx={{flex: 1, overflow: 'hidden', position: 'relative'}}>
                                    <ViewerContainer
                                        showHeader={false}
                                        selectedAnnotations={(() => {
                                            const groundTruth = selectedComparison?.target_ground_truth;
                                            const flat = [];
                                            // Ground truth format: {"field_name": {"value": "...", "bbox": [...], "page": 1}}
                                            if (!groundTruth || typeof groundTruth !== 'object') return flat;

                                            // Ground truth bboxes are stored as raw API coordinates (with MARGIN=2)
                                            // When using File object, just use bbox directly like ToolResultsPage does
                                            Object.entries(groundTruth).forEach(([fieldName, fieldData]) => {
                                                if (fieldData && fieldData.bbox && Array.isArray(fieldData.bbox) && fieldData.bbox.length === 4) {
                                                    const page = fieldData.page ?? 1;
                                                    const uid = `${page}_entity_${fieldData.bbox.join('_')}`;
                                                    flat.push({
                                                        page,
                                                        bbox: fieldData.bbox,
                                                        type: 'entity',
                                                        __uid: uid,
                                                        label: fieldName,
                                                        text: fieldData.value
                                                    });
                                                }
                                            });
                                            return flat;
                                        })()}
                                        setSelectedAnnotations={() => {}}
                                        groupMeta={{}}
                                        setGroupMeta={() => {}}
                                        file_input={targetPdfFile}
                                        hideSelectionPanel
                                        readOnlyHighlights
                                        forceAllSelected
                                        hideToolbar={false}
                                        showZoomControls
                                        showAnnotateControls={false}
                                        preventAutoExtract
                                        containerWidth="100%"
                                        paperSx={{maxWidth: 'none', mt: 0, height: '100%'}}
                                        containerSx={{mt: 0, pt: 0, height: '100%'}}
                                    />
                                </Box>
                            </Box>

                            {/* Right: Target Document with Extracted Annotations */}
                            <Box display="flex" flexDirection="column">
                                <Typography variant="subtitle1" gutterBottom>
                                    Extracted
                                </Typography>
                                <Box sx={{flex: 1, overflow: 'hidden', position: 'relative'}}>
                                    <ViewerContainer
                                        showHeader={false}
                                        selectedAnnotations={(() => {
                                            const extractedData = selectedComparison?.extracted_data;
                                            const flat = [];
                                            // Extracted data format: {"field_name": {"value": "...", "bbox": [...], "page": 1}}
                                            if (!extractedData || typeof extractedData !== 'object') return flat;

                                            // Extracted data needs adjustment - shift right and down
                                            const OFFSET_X = 8;  // shift right
                                            const OFFSET_Y = 8;  // shift down
                                            const adjustBbox = (bbox) => {
                                                if (!Array.isArray(bbox) || bbox.length !== 4) return bbox;
                                                return [
                                                    bbox[0] + OFFSET_X,
                                                    bbox[1] + OFFSET_Y,
                                                    bbox[2] + OFFSET_X,
                                                    bbox[3] + OFFSET_Y
                                                ];
                                            };

                                            Object.entries(extractedData).forEach(([fieldName, fieldData]) => {
                                                if (fieldData && fieldData.bbox && Array.isArray(fieldData.bbox) && fieldData.bbox.length === 4) {
                                                    const adjustedBbox = adjustBbox(fieldData.bbox);
                                                    const page = fieldData.page ?? 1;
                                                    const uid = `${page}_entity_${adjustedBbox.join('_')}`;
                                                    flat.push({
                                                        page,
                                                        bbox: adjustedBbox,
                                                        type: 'entity',
                                                        __uid: uid,
                                                        label: fieldName,
                                                        text: fieldData.value
                                                    });
                                                }
                                            });
                                            return flat;
                                        })()}
                                        setSelectedAnnotations={() => {}}
                                        groupMeta={{}}
                                        setGroupMeta={() => {}}
                                        file_input={targetPdfFile}
                                        hideSelectionPanel
                                        readOnlyHighlights
                                        forceAllSelected
                                        hideToolbar={false}
                                        showZoomControls
                                        showAnnotateControls={false}
                                        preventAutoExtract
                                        containerWidth="100%"
                                        paperSx={{maxWidth: 'none', mt: 0, height: '100%'}}
                                        containerSx={{mt: 0, pt: 0, height: '100%'}}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setComparisonDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
